'use client';

import { useCallback } from 'react';
import { useCurrencyStore } from '@/store';
import { formatAmount, formatUsd, USD_FALLBACK } from '@/lib/currency/convert';

// The single public API for rendering money in the UI. Components never touch
// rates, symbols, or rounding directly — they call format(usdAmount) and get
// the visitor's selected currency: 12.5 -> "$12.50" or "LL 1,120,000".
export function useCurrency() {
    const currencies = useCurrencyStore((state) => state.currencies);
    const selectedCode = useCurrencyStore((state) => state.selectedCode);
    const setCurrency = useCurrencyStore((state) => state.setCurrency);

    const currency = currencies.find((c) => c.code === selectedCode) ?? USD_FALLBACK;
    // The base currency can change (admin action), so it's resolved from the
    // live list, not assumed to be USD. Falls back to USD_FALLBACK only
    // pre-hydration, before any real currency config has loaded.
    const baseCurrency = currencies.find((c) => c.isBase) ?? USD_FALLBACK;

    const format = useCallback(
        (usdAmount: number) => formatUsd(usdAmount, currency),
        [currency]
    );

    // Formats an already-base-denominated amount for base-currency contexts
    // (admin, the "≈ x" secondary line at checkout) — no conversion applied.
    const formatBase = useCallback(
        (baseAmount: number) => formatAmount(baseAmount, baseCurrency),
        [baseCurrency]
    );

    return {
        currency,
        currencies,
        setCurrency,
        format,
        formatBase,
        baseCurrency,
        isBase: currency.code === baseCurrency.code,
    };
}
